// frontend/src/components/MovieCard.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Heart, Bookmark, Film } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAppContext } from '../context/AppContext';

interface Movie {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  favoritesCount?: number;
  reviewsCount?: number;
}

interface MovieCardProps {
  movie: Movie;
}

const seasonColors: Record<string, string> = {
  winter: 'border-romcom-blue',
  spring: 'border-romcom-pink',
  summer: 'border-romcom-gold',
  autumn: 'border-romcom-red',
};
const seasonGradients: Record<string, string> = {
  winter: 'from-romcom-blue to-[#1a2f4a]',
  spring: 'from-romcom-pink to-romcom-mauve',
  summer: 'from-romcom-gold to-romcom-red',
  autumn: 'from-romcom-red to-[#8b3a3a]',
};

const holidayLabels: Record<string, { label: string; color: string }> = {
  christmas: { label: '🎄 Christmas', color: 'bg-romcom-red/20 text-romcom-red' },
  valentine: { label: '💝 Valentine', color: 'bg-romcom-pink/20 text-romcom-mauve' },
  new_year: { label: '🎆 New Year', color: 'bg-romcom-blue/20 text-romcom-blue' },
  halloween: { label: '🎃 Halloween', color: 'bg-romcom-gold/20 text-romcom-gold' },
};

const MovieCard: React.FC<MovieCardProps> = ({ movie }) => {
  const navigate = useNavigate();
  const { state, toggleLike, toggleWatchlist } = useAppContext();

  const isLiked = state.likedMovies.includes(movie.id);
  const isWatchlisted = state.watchlist.includes(movie.id);

  const season = movie.season || 'spring';
  const borderColor = seasonColors[season] || 'border-pink-300';
  const gradient = seasonGradients[season] || 'from-pink-400 to-rose-500';

  const handleLikeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleLike(movie.id);
  };

  const handleWatchlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleWatchlist(movie.id);
  };

  const rating = movie.voteAverage ? (movie.voteAverage / 2) : 0; // TMDB is out of 10, convert to 5
  const holidayInfo = movie.holiday && movie.holiday !== 'none' ? holidayLabels[movie.holiday] : null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ duration: 0.3 }}
      onClick={() => navigate(`/movies/${movie.id}`)}
      className={`bg-[#faf8f6] rounded-xl shadow-lg overflow-hidden cursor-pointer border-t-4 ${borderColor}`}

    >
      {/* Poster */}
      <div className="relative h-64 overflow-hidden">
        {movie.posterUrl ? (
          <img
            src={movie.posterUrl}
            alt={movie.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to gradient if image fails
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
            <div className="text-center px-4">
              <Film className="w-12 h-12 text-white mx-auto mb-2" />
              <p className="text-white font-bold text-lg leading-tight">{movie.title}</p>
            </div>
          </div>
        )}

        {/* Overlay gradient for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

        {/* Action buttons */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          <button
            onClick={handleLikeClick}
            className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/40 transition-all"
            title={isLiked ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart
              className={`w-5 h-5 ${isLiked ? 'text-red-500 fill-current' : 'text-white'}`}
            />
          </button>
          <button
            onClick={handleWatchlistClick}
            className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/40 transition-all"
            title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
          >
            <Bookmark
              className={`w-5 h-5 ${isWatchlisted ? 'text-yellow-400 fill-current' : 'text-white'}`}
            />
          </button>
        </div>

        {/* Release year badge */}
        {movie.releaseYear && (
          <div className="absolute bottom-3 left-3">
            <span className="bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
              {movie.releaseYear}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-bold text-gray-800 text-base leading-tight mb-2 line-clamp-1">
          {movie.title}
        </h3>

        {/* Overview */}
        {movie.overview && (
          <p className="text-gray-500 text-xs mb-3 line-clamp-2 leading-relaxed">
            {movie.overview}
          </p>
        )}

        {/* Rating */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-3.5 h-3.5 ${
                  i < Math.floor(rating)
                    ? 'text-yellow-400 fill-current'
                    : i < rating
                    ? 'text-yellow-400 fill-current opacity-50'
                    : 'text-gray-300'
                }`}
              />
            ))}
            <span className="text-xs font-semibold text-gray-600 ml-1">
              {rating.toFixed(1)}
            </span>
          </div>

          {/* Season badge */}
          {movie.season && (
            <span className="text-xs text-gray-500 capitalize">
              {movie.season === 'winter' ? '❄️' :
               movie.season === 'spring' ? '🌸' :
               movie.season === 'summer' ? '☀️' : '🍂'} {movie.season}
            </span>
          )}
        </div>

        {/* Holiday badge */}
        {holidayInfo && (
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${holidayInfo.color} mb-2`}>
            {holidayInfo.label}
          </span>
        )}

        {/* Tropes */}
        {movie.tropes && movie.tropes.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {movie.tropes.slice(0, 2).map((trope) => (
              <span
                key={trope}
                className="text-xs bg-romcom-pink/15 text-romcom-mauve px-2 py-0.5 rounded-full"

              >
                {trope}
              </span>
            ))}
            {movie.tropes.length > 2 && (
              <span className="text-xs text-gray-400">+{movie.tropes.length - 2}</span>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
          <span>❤️ {movie.favoritesCount || 0}</span>
          <span>💬 {movie.reviewsCount || 0} reviews</span>
        </div>
      </div>
    </motion.div>
  );
};

export default MovieCard;
